import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { ArrowUp, Check } from "@dreamhub/designsystem/icons";
import { DraftEmailCopy as CopyIcon } from "@dreamhub/designsystem/icons";
import { useClipboard } from "@dreamhub/designsystem";
import { cn } from "@/utils/utils";
import {
  DraftEmailPayload,
  draftBody,
  draftRecipientPersonIds,
  draftSubject,
  selectedDraftVersion
} from "@/entities/dreamerEmail/types";
import { usePeople } from "@/entities/people/hooks";
import DraftEmailVersionNav from "./DraftEmailVersionNav";
import DraftEmailRegenerate from "./DraftEmailRegenerate";
import DraftEmailSubject from "./DraftEmailSubject";
import DraftEmailSendButton from "./DraftEmailSendButton";

// Widths for the body loading skeleton, sized to read like lines of an email.
const SKELETON_LINES = [
  { id: "l1", width: "45%" },
  { id: "l2", width: "100%" },
  { id: "l3", width: "92%" },
  { id: "l4", width: "100%" },
  { id: "l5", width: "60%" }
];

// Matches the guidance limit the regenerate endpoint enforces.
const MAX_GUIDANCE_LENGTH = 4096;

interface Props {
  payload: DraftEmailPayload;
  // The Retry control only renders when a handler is provided. Guidance is
  // optional — without it the draft is regenerated from the same context.
  onRegenerate?: (guidance?: string) => void;
  // A regeneration is in flight: the result arrives over the WebSocket, so this
  // is tracked by the caller rather than carried on the payload.
  isGenerating?: boolean;
  // Persists which version the card shows. onFailure restores the previous
  // version when the write is rejected.
  onSelectVersion?: (version: number, onFailure: () => void) => void;
  className?: string;
}

// Draft email affordance rendered inside a Dreamer chat message: the card
// (subject + body + version navigator), the action row (Copy + Send), and the
// follow-up prompt. Body is Dreamer-generated plain text, rendered with
// preserved whitespace. Sending is client-side — the card opens the user's mail
// app pre-filled.
const DraftEmailCard = ({
  payload,
  onRegenerate,
  isGenerating = false,
  onSelectVersion,
  className
}: Props) => {
  const { t } = useTranslation();
  const { versions } = payload;

  // Versions are addressed by their 1-based version number, not by their
  // position: the payload can arrive without the versions the card never showed.
  const fallbackVersion = selectedDraftVersion(payload)?.version ?? 1;
  const [activeVersionNumber, setActiveVersionNumber] =
    useState(fallbackVersion);

  // Follow the payload when the server moves the selected version — a completed
  // regeneration selects the version it appended. useLayoutEffect (not
  // useEffect) so the switch happens in the same synchronous pass before paint,
  // otherwise the card flashes the previous version for one frame.
  useLayoutEffect(() => {
    setActiveVersionNumber(fallbackVersion);
  }, [fallbackVersion]);

  const activeIndex = Math.max(
    versions.findIndex((version) => version.version === activeVersionNumber),
    0
  );
  const activeVersion = versions[activeIndex];
  const subject = draftSubject(activeVersion);
  const body = draftBody(activeVersion);

  // Recipients on the draft carry CRM identity (personId), not an email
  // address — resolve through the people store (cached, no dedicated
  // endpoint needed). Name-only recipients with no personId are dropped;
  // there's nothing to resolve them against.
  const recipientPersonIds = draftRecipientPersonIds(activeVersion);
  const {
    entities: recipientPeople,
    isLoading: isResolvingRecipients,
    isError: recipientResolutionFailed
  } = usePeople(recipientPersonIds, {
    enabled: recipientPersonIds.length > 0
  });
  const recipientEmails = useMemo(
    () =>
      Array.from(
        new Set(
          recipientPeople
            .map((person) => person?.email)
            .filter((email): email is string => !!email)
        )
      ),
    [recipientPeople]
  );

  const selectVersion = useCallback(
    (index: number) => {
      const version = versions[index];
      if (!version) return;
      const previousVersionNumber = activeVersionNumber;
      setActiveVersionNumber(version.version);
      onSelectVersion?.(version.version, () =>
        setActiveVersionNumber(previousVersionNumber)
      );
    },
    [versions, activeVersionNumber, onSelectVersion]
  );

  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [guidance, setGuidance] = useState("");

  const submitRegenerate = useCallback(() => {
    const trimmed = guidance.trim();
    setIsGuidanceOpen(false);
    setGuidance("");
    onRegenerate?.(trimmed || undefined);
  }, [guidance, onRegenerate]);

  const { hasCopied, onCopy } = useClipboard(body);

  const bodyRef = useRef<HTMLDivElement>(null);
  const [preservedHeight, setPreservedHeight] = useState<number | null>(null);

  // While a regeneration is in flight, hold the body at its last content height
  // so the card doesn't collapse before the new version arrives.
  useLayoutEffect(() => {
    if (!isGenerating && bodyRef.current) {
      setPreservedHeight(bodyRef.current.offsetHeight);
    }
  }, [isGenerating, body]);

  return (
    <div className="flex w-full flex-col gap-2" data-test="draft-email">
      <div
        className={cn(
          "flex w-full flex-col overflow-hidden rounded-2xl border-[0.5px] border-border-border1 bg-surface-elevation2 shadow-[0px_4px_12px_-8px_rgba(12,17,29,0.24)]",
          className
        )}
        data-test="draft-email-card"
      >
        <div className="flex items-start gap-2 p-4">
          {isGenerating ? (
            <div className="flex-1" />
          ) : (
            <DraftEmailSubject subject={subject} className="flex-1" />
          )}
          {onRegenerate ? (
            <DraftEmailRegenerate
              onRegenerate={() => setIsGuidanceOpen((open) => !open)}
              isGenerating={isGenerating}
            />
          ) : null}
        </div>

        <div
          ref={bodyRef}
          className="min-h-24 max-h-[640px] overflow-y-auto px-4 pb-3"
          style={
            isGenerating && preservedHeight
              ? { minHeight: preservedHeight }
              : undefined
          }
        >
          {isGenerating ? (
            <div
              className="flex flex-col gap-2 py-1"
              data-test="draft-email-body-skeleton"
              aria-hidden="true"
            >
              {SKELETON_LINES.map((line) => (
                <div
                  key={line.id}
                  className="h-3 animate-pulse rounded bg-gradient-to-r from-white-8 to-white-4"
                  style={{ width: line.width }}
                />
              ))}
            </div>
          ) : (
            <Text
              as="p"
              variant="body"
              className="cursor-default whitespace-pre-wrap break-words"
              data-test="draft-email-body"
            >
              {body}
            </Text>
          )}
        </div>

        {isGuidanceOpen && !isGenerating ? (
          <div className="px-4 pb-3">
            <Input
              autoFocus
              value={guidance}
              maxLength={MAX_GUIDANCE_LENGTH}
              className="pr-10"
              placeholder={t("draftEmail.guidancePlaceholder")}
              data-test="draft-email-guidance"
              aria-label={t("draftEmail.guidancePlaceholder")}
              onChange={(event) => setGuidance(event.target.value)}
              onKeyDown={(event) => {
                // Enter confirms an IME candidate; it is not a submit.
                if (event.nativeEvent.isComposing) return;
                if (event.key === "Enter") {
                  event.preventDefault();
                  submitRegenerate();
                } else if (event.key === "Escape") {
                  setIsGuidanceOpen(false);
                }
              }}
              inlineElement={
                <IconButton
                  variant="icon"
                  size="sm"
                  iconColor="primary"
                  onClick={submitRegenerate}
                  data-test="draft-email-guidance-submit"
                  aria-label={t("draftEmail.retry")}
                >
                  <ArrowUp />
                </IconButton>
              }
            />
          </div>
        ) : null}

        {versions.length > 1 ? (
          <DraftEmailVersionNav
            current={activeIndex + 1}
            total={versions.length}
            onPrev={() => selectVersion(activeIndex - 1)}
            onNext={() => selectVersion(activeIndex + 1)}
            disabled={isGenerating}
          />
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <IconButton
              variant="icon"
              size="lg"
              iconColor="primary"
              onClick={onCopy}
              disabled={isGenerating || !activeVersion}
              data-test="draft-email-copy"
              aria-label={t("draftEmail.copy")}
            >
              {hasCopied ? <Check /> : <CopyIcon />}
            </IconButton>
          </TooltipTrigger>
          <TooltipContent>
            {t(hasCopied ? "draftEmail.copied" : "draftEmail.copy")}
          </TooltipContent>
        </Tooltip>

        <DraftEmailSendButton
          subject={subject}
          body={body}
          recipientEmails={recipientEmails}
          disabled={
            isGenerating ||
            !activeVersion ||
            isResolvingRecipients ||
            recipientResolutionFailed
          }
        />
      </div>

      <Text as="p" variant="bodySecondary" data-test="draft-email-followup">
        {t("draftEmail.followUpPrompt")}
      </Text>
    </div>
  );
};

export default DraftEmailCard;
